// ROOM 404: Header App Bar & Quick Actions

import React from 'react';
import { AlertTriangle, Bell, Calendar, ShieldCheck, Sparkles, UserCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import type { SocketStatus } from '../services/socket';

interface HeaderProps {
  socketStatus: SocketStatus;
  unreadNotificationsCount?: number;
  onOpenEvents: () => void;
  onOpenNotifications: () => void;
  onOpenReport: () => void;
  onOpenAdmin: () => void;
}

export const Header: React.FC<HeaderProps> = ({
  socketStatus,
  unreadNotificationsCount = 0,
  onOpenEvents,
  onOpenNotifications,
  onOpenReport,
  onOpenAdmin,
}) => {
  const { session, isStaff, isVerifier } = useAuth();

  const getStatusColor = () => {
    switch (socketStatus) {
      case 'connected':
        return 'bg-emerald-500 shadow-[0_0_8px_#10b981]';
      case 'connecting':
        return 'bg-amber-400 animate-pulse';
      case 'error':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  return (
    <header className="sticky top-0 z-30 bg-[#090a0f]/90 backdrop-blur-md border-b border-[#1f2433] px-4 py-2.5">
      <div className="max-w-md mx-auto flex items-center justify-between">
        {/* Brand & Connection State */}
        <div className="flex items-center space-x-2">
          <div className="flex items-center space-x-1.5">
            <span className="font-mono text-base font-extrabold tracking-wider text-white">
              ROOM <span className="text-emerald-400">404</span>
            </span>
            <div
              className={`w-2 h-2 rounded-full transition-colors ${getStatusColor()}`}
              title={`Live Chat Socket: ${socketStatus}`}
            />
          </div>

          {session?.profile?.standing && (
            <span className="text-[10px] uppercase font-mono px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
              Verified
            </span>
          )}
        </div>

        {/* Quick Actions */}
        <div className="flex items-center space-x-2">
          {/* Points Chip */}
          {session?.profile && (
            <div className="flex items-center space-x-1 bg-[#12141c] border border-[#1f2433] rounded-full px-2 py-0.5 text-xs text-amber-400 font-mono">
              <Sparkles className="w-3 h-3 text-amber-400" />
              <span>{session.profile.totalPoints}</span>
            </div>
          )}

          {/* Events shortcut */}
          <button
            onClick={onOpenEvents}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-100 hover:bg-[#12141c] transition-colors"
            title="Events"
          >
            <Calendar className="w-4 h-4" />
          </button>

          {/* Report safety shortcut */}
          <button
            onClick={onOpenReport}
            className="p-1.5 rounded-lg text-gray-400 hover:text-amber-400 hover:bg-[#12141c] transition-colors"
            title="Safety & Report"
          >
            <AlertTriangle className="w-4 h-4" />
          </button>

          {/* Notifications */}
          <button
            onClick={onOpenNotifications}
            className="relative p-1.5 rounded-lg text-gray-400 hover:text-gray-100 hover:bg-[#12141c] transition-colors"
            title="Notifications"
          >
            <Bell className="w-4 h-4" />
            {unreadNotificationsCount > 0 && (
              <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-[#090a0f]" />
            )}
          </button>

          {/* Verifier / Admin Staff Portal */}
          {(isStaff || isVerifier) && (
            <button
              onClick={onOpenAdmin}
              className="p-1.5 rounded-lg text-emerald-400 hover:bg-emerald-500/10 border border-emerald-500/30 transition-colors"
              title="Staff Portal (Verifier & Admin)"
            >
              <ShieldCheck className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </header>
  );
};
